/* eslint-disable @typescript-eslint/no-explicit-any */
import { useParams } from "@tanstack/react-router"
import type * as Api from "../@client/api"
import { UseMutationResult } from "@tanstack/react-query"
import { FormProps } from "./forms/mantine-form-context"
import { Form as FormContext } from "./forms/Form"
import { UseFormReturnType } from "@mantine/form"
import { PathProxy } from "../utils/forms-proxy"
import { LoadingOverlay } from "@mantine/core"
import { usePostMutation } from "../typed-client/hooks/use-post-mutation"
import { UsePostMutationQueryOptions } from "../typed-client/types"
import { DefaultModal } from "./default-modal"

type ChildrenFunction<Values, ActionName extends keyof Api.POST, T> = (
  form: UseFormReturnType<Values>,
  _: PathProxy<T, T>,
  mutation: UseMutationResult<
    Api.Output[ActionName],
    Error,
    Api.Input[ActionName],
    unknown
  >
) => React.ReactNode | React.ReactNode[]

type Children<Values, ActionName extends keyof Api.POST, T> =
  | React.ReactNode
  | React.ReactNode[]
  | ChildrenFunction<Values, ActionName, T>

export type TypedFormProps<Values, ActionName extends keyof Api.POST> = {
  url: ActionName
  transform: (
    values: Values,
    e: React.FormEvent<HTMLFormElement> | undefined
  ) => Api.Input[ActionName]
  onError?: (
    error: Error,
    variables: Api.Input[ActionName],
    context: unknown,
    f: UseFormReturnType<Values>
  ) => void
  options?: UsePostMutationQueryOptions<ActionName>
  requestInit?: RequestInit
  onSuccess?: (
    data: Api.Output[ActionName],
    variables: Api.Input[ActionName],
    context: unknown
  ) => void
  children: Children<Values, ActionName, Values>
  //   onSuccess?: (f: UseFormReturnType<Api.Output[ActionName]>) => void
  //   children: Children<Values, ActionName, Values>
} & Omit<FormProps<Values>, "onSubmit" | "children">

export function Form<
  Values extends Record<string, any>,
  ActionName extends keyof Api.POST,
>({
  url,
  children,
  options,
  requestInit,
  onSuccess,
  transform,
  onError,
  ...props
}: TypedFormProps<Values, ActionName>) {
  const params = useParams({ strict: false }) as any

  const headers =
    params["id"] !== undefined
      ? { ...requestInit?.headers, "X-App-StreamId": params["id"] }
      : requestInit?.headers

  const action = usePostMutation<ActionName>(url, options, {
    ...requestInit,
    headers,
  })

  return (
    <>
      <LoadingOverlay visible={action.isPending} />
      <FormContext<Values>
        onSubmit={(values, e, f) =>
          action.mutate(transform(values, e), {
            onSuccess: onSuccess!,
            onError: (e, v, c) => {
              onError && onError(e, v, c, f)
            },
          })
        }
        {...props}
      >
        {typeof children === "function"
          ? (f, _) => children(f as any, _ as any, action)
          : children}
      </FormContext>
    </>
  )
}

export function ModalForm<
  Values extends Record<string, any>,
  ActionName extends keyof Api.POST,
>({
  opened,
  onClose,
  title,
  closeOnClickOutside,
  ...props
}: {
  title: string
  opened: boolean
  closeOnClickOutside?: boolean
  onClose: () => void
} & TypedFormProps<Values, ActionName>) {
  return (
    <DefaultModal
      closeOnClickOutside={closeOnClickOutside}
      onClose={() => {
        onClose()
      }}
      opened={opened}
      title={title}
    >
      <Form {...props} />
    </DefaultModal>
  )
}
